import { useEffect } from "react";
import { useConfig } from "../../config/ConfigContext";
import { useHomeUi } from "../../context/HomeUiContext";
import { normalizeCheckoutConfig } from "../../utils/checkoutFormConfig";
import formatPrice from "../../utils/formatPrice";
import { computeOrderTotals } from "../../utils/orderPricing";
import { useToys } from "../Toy/ToysContext";

export default function CartDrawer({
  onRequestQuote,
  onMissingRentalDate,
  onGoCheckout,
}) {
  const { drawerOpen, closeDrawer, showStockLimit } = useHomeUi();
  const { cart, setCart, toys, reservationDate, availabilityByToyId } =
    useToys();
  const config = useConfig();
  const noCheckout = config.noCheckout || config.id === "versionNoCheckout";

  const totalItemPrice = cart.reduce(
    (sum, item) =>
      sum + item.quantity * item.toy.fields[config.itemPriceFieldTTC],
    0,
  );
  const {
    deliveryFee,
    totalDiscount,
    totalPriceDisplay: totalPrice,
  } = computeOrderTotals(config, totalItemPrice);

  const n = cart.reduce((acc, item) => acc + item.quantity, 0);

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const dateTxt = reservationDate
    ? reservationDate.toLocaleDateString("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "No date selected — choose one above";

  const lineMax = (item) => {
    if (
      reservationDate &&
      Object.prototype.hasOwnProperty.call(availabilityByToyId, item.toy.id)
    ) {
      return availabilityByToyId[item.toy.id];
    }
    return item.toy.maxStock ?? Number.POSITIVE_INFINITY;
  };

  const updateLineQty = (toyId, delta) => {
    const ix = cart.findIndex((c) => c.toy.id === toyId);
    if (ix === -1) return;
    const item = cart[ix];
    const max = lineMax(item);
    if (delta > 0 && item.quantity >= max) {
      showStockLimit(item.toy.fields["Name"] || "this item", max);
      return;
    }

    setCart((prev) => {
      const next = [...prev];
      const j = next.findIndex((c) => c.toy.id === toyId);
      if (j === -1) return prev;
      const q = next[j].quantity + delta;
      if (q <= 0) {
        next.splice(j, 1);
      } else {
        next[j] = { ...next[j], quantity: q };
      }
      next.sort((a, b) => {
        const ia = toys.findIndex((t) => t.id === a.toy.id);
        const ib = toys.findIndex((t) => t.id === b.toy.id);
        return ia - ib;
      });
      return next;
    });
  };

  const removeLine = (toyId) => {
    setCart((prev) => prev.filter((c) => c.toy.id !== toyId));
  };

  const handleCheckoutClick = () => {
    if (!reservationDate) {
      onMissingRentalDate?.();
      return;
    }
    onGoCheckout?.();
  };

  const cfg = normalizeCheckoutConfig(config);
  const showPartnerDiscount =
    cfg.showPartnerDiscountInSummary && totalDiscount > 0;
  const showDeliveryFee =
    cfg.showDeliveryFeeInSummary && deliveryFee > 0;
  const hasPricingExtras = showPartnerDiscount || showDeliveryFee;

  return (
    <>
      <div
        className={`overlay ${drawerOpen ? "open" : ""}`}
        onClick={closeDrawer}
        aria-hidden={!drawerOpen}
      />
      <aside
        className={`drawer ${drawerOpen ? "open" : ""}`}
        aria-hidden={!drawerOpen}
      >
        <div className="dwr-head">
          <span className="dwr-title">My Selection</span>
          <button
            type="button"
            className="dwr-close"
            onClick={closeDrawer}
            aria-label="Close cart"
          >
            ×
          </button>
        </div>
        <div className="dwr-date">
          <span>📅</span>
          <span className="dwr-date-txt">{dateTxt}</span>
          <button
            type="button"
            className="dwr-date-chg"
            onClick={() => {
              closeDrawer();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            Change
          </button>
        </div>

        {n === 0 ? (
          <div className="dwr-empty">
            <div className="dwr-empty-icon">🛒</div>
            <div className="dwr-empty-txt">
              Your selection is empty.
              <br />
              Add toys from the catalogue.
            </div>
          </div>
        ) : (
          <div className="dwr-body">
            <div className="dwr-items">
              {cart.map((item) => {
                const p = item.toy.fields["Photos"]?.[0];
                const price = item.toy.fields[config.itemPriceFieldTTC];
                const sub = item.quantity * price;
                return (
                  <div className="dwr-item" key={item.toy.id}>
                    <div className="dwr-img">
                      {p?.url ? <img src={p.url} alt="" /> : <span>🧸</span>}
                    </div>
                    <div className="dwr-info">
                      <div className="dwr-name">{item.toy.fields["Name"]}</div>
                      <div className="dwr-unit">
                        €{formatPrice(price)} / day
                      </div>
                    </div>
                    <div className="dwr-right">
                      <div className="dwr-price">€{formatPrice(sub)}</div>
                      <div className="dwr-qty">
                        <button
                          type="button"
                          className="dqbtn"
                          onClick={() => updateLineQty(item.toy.id, -1)}
                        >
                          −
                        </button>
                        <span className="dqnum">{item.quantity}</span>
                        <button
                          type="button"
                          className="dqbtn"
                          onClick={() => updateLineQty(item.toy.id, 1)}
                        >
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        className="dwr-rm"
                        onClick={() => removeLine(item.toy.id)}
                        aria-label="Remove"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="dwr-foot">
              {showPartnerDiscount ? (
                <div className="os-tax">
                  <span>Partner Discount</span>
                  <span className="pricing-discount-val">
                    −{formatPrice(totalDiscount)} €
                  </span>
                </div>
              ) : null}
              {showDeliveryFee ? (
                <div className="os-tax">
                  <span>Delivery Fee</span>
                  <span className="pricing-fee-val">
                    +{formatPrice(deliveryFee)} €
                  </span>
                </div>
              ) : null}
              {hasPricingExtras ? <div className="dwr-divider" /> : null}
              <div className="dwr-total-row">
                <span className="dwr-total-lbl">Total</span>
                <span className="dwr-total-val">
                  €{formatPrice(totalPrice)}
                </span>
              </div>
              <button
                type="button"
                className="dwr-cta"
                onClick={handleCheckoutClick}
              >
                {noCheckout ? "Make a request" : "Check out"}
                <svg viewBox="0 0 24 24" aria-hidden>
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
              <div className="dwr-note">
                You will receive a confirmation email once booked.
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
